import { useEffect, useMemo, useState } from "react";
import API_BASE from "../../config";
import { extractErrorMessage } from "../../utils/apiError";
import NotificationBanner from "../../components/NotificationBanner";
import s from "./MyTimesheet.module.css";

function getUser() {
  try { return JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "null"); }
  catch { return null; }
}

function resolveEmployeeCode(u) {
  if (!u) return "";
  if (u.employeeCode) return u.employeeCode;
  if (u.linkedEntity && u.linkedEntity.code) return u.linkedEntity.code;
  return "";
}

const MONTHS = ["January","February","March","April","May","June",
                "July","August","September","October","November","December"];

function isoDate(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const DOW = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

/** 7×6 Mon→Sun grid for the calendar view. Same shape as MyCalendarPage's
 *  grid so the two pages feel like a family. Cells from neighbouring
 *  months are flagged `muted` so they render dimmed but still occupy a
 *  slot, keeping the layout stable as the user navigates. */
function buildGrid(year, month) {
  const first = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startDow = (first.getDay() + 6) % 7; // Mon=0...Sun=6
  const cells = [];
  const prevDays = new Date(year, month, 0).getDate();
  for (let i = startDow - 1; i >= 0; i--) {
    const d = prevDays - i;
    const pm = month === 0 ? 11 : month - 1;
    const py = month === 0 ? year - 1 : year;
    cells.push({ day: d, month: pm, year: py, muted: true });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, month, year, muted: false });
  }
  const nm = month === 11 ? 0 : month + 1;
  const ny = month === 11 ? year + 1 : year;
  let n = 1;
  while (cells.length < 42) {
    cells.push({ day: n++, month: nm, year: ny, muted: true });
  }
  return cells.map(c => ({
    ...c,
    iso: `${c.year}-${String(c.month + 1).padStart(2, "0")}-${String(c.day).padStart(2, "0")}`,
  }));
}

function fmtTime(hhmm) {
  if (!hhmm) return "—";
  const [h, m] = String(hhmm).split(":");
  const n = Number(h);
  if (!Number.isFinite(n)) return hhmm;
  const period = n >= 12 ? "PM" : "AM";
  const display = ((n + 11) % 12) + 1;
  return `${display}:${m || "00"} ${period}`;
}

function nowHHMM() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/** Live wall-clock string, refreshed once per second. */
function useLiveClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const time = now.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", second: "2-digit" });
  const date = now.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  return { time, date };
}

export default function MyTimesheetPage() {
  const user = getUser() || {};
  const employeeCode = resolveEmployeeCode(user);

  const today = new Date();
  const [month, setMonth] = useState(today.getMonth());
  const [year,  setYear]  = useState(today.getFullYear());

  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState("");
  const [pulse, setPulse]     = useState(0);
  // "list" (default) is the original table view; "calendar" is the
  // new month-grid view. Both render from the same `rows` dataset so
  // toggling is a pure render-layer choice — no extra fetches.
  const [viewMode, setViewMode] = useState("list");

  const clock = useLiveClock();

  // ── Load month ───────────────────────────────────────────────
  useEffect(() => {
    if (!employeeCode) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    fetch(`${API_BASE}/api/timesheets?employeeCode=${employeeCode}&year=${year}&month=${month + 1}`)
      .then(r => r.ok ? r.json() : [])
      .then(data => { if (!cancelled) setRows(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setRows([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [employeeCode, year, month, pulse]);

  // ── Today's row + period totals ──────────────────────────────
  const todayIso = isoDate();
  const todayRow = useMemo(
    () => rows.find(r => String(r.date) === todayIso) || null,
    [rows, todayIso]
  );

  // O(1) date → row lookup for the calendar view.
  const rowsByDate = useMemo(() => {
    const m = new Map();
    for (const r of rows) m.set(String(r.date), r);
    return m;
  }, [rows]);

  // Build the month grid eagerly so we can also use it for the calendar
  // view's keyboard nav and animation triggers without rebuilding inside
  // the render path.
  const grid = useMemo(() => buildGrid(year, month), [year, month]);

  const totals = useMemo(() => {
    let worked = 0, ot = 0, otApproved = 0, daysPresent = 0;
    for (const r of rows) {
      const w = r.workedHours || 0;
      worked += w;
      if (w > 0) daysPresent += 1;
      if ((r.overtimeHours || 0) > 0) {
        ot += r.overtimeHours;
        if ((r.overtimeStatus || "").toUpperCase() === "APPROVED") otApproved += r.overtimeHours;
      }
    }
    return {
      worked: Math.round(worked * 10) / 10,
      ot: Math.round(ot * 10) / 10,
      otApproved: Math.round(otApproved * 10) / 10,
      daysPresent,
    };
  }, [rows]);

  // ── Actions: Clock in / out / Request OT ─────────────────────
  async function clockIn() {
    setBusy(true); setError("");
    try {
      const body = {
        employeeCode,
        date: todayIso,
        timeIn: nowHHMM(),
        breakMinutes: 0,
        status: "Present",
      };
      const res = await fetch(`${API_BASE}/api/timesheets`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, "Clock-in failed"));
      setPulse(p => p + 1);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function clockOut() {
    if (!todayRow?.id) return;
    setBusy(true); setError("");
    try {
      const out = nowHHMM();
      // Worked hours: simple end - start - break
      const [sh, sm] = (todayRow.timeIn || "00:00").split(":").map(Number);
      const [eh, em] = out.split(":").map(Number);
      const minutes = (eh * 60 + em) - (sh * 60 + sm) - (todayRow.breakMinutes || 0);
      const worked = Math.max(0, Math.round((minutes / 60) * 100) / 100);
      const body = { ...todayRow, timeOut: out, workedHours: worked };
      const res = await fetch(`${API_BASE}/api/timesheets/${todayRow.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, "Clock-out failed"));
      setPulse(p => p + 1);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function requestOvertime(row) {
    setBusy(true); setError("");
    try {
      const res = await fetch(`${API_BASE}/api/timesheets/${row.id}/request-overtime`, {
        method: "POST",
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, "Request failed"));
      setPulse(p => p + 1);
      window.dispatchEvent(new CustomEvent("approvals:changed"));
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function cancelOvertime(row) {
    setBusy(true); setError("");
    try {
      const res = await fetch(`${API_BASE}/api/timesheets/${row.id}/cancel-overtime`, {
        method: "POST",
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, "Cancel failed"));
      setPulse(p => p + 1);
      window.dispatchEvent(new CustomEvent("approvals:changed"));
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  // ── Today's status display ───────────────────────────────────
  const statusKind = !todayRow ? "out"
    : todayRow.timeOut ? "done"
    : "in";

  // ── Render ───────────────────────────────────────────────────
  if (!employeeCode) {
    return (
      <div className={s.page}>
        <div className={s.emptyState}>
          <div className={s.emptyEmoji}>🔒</div>
          <h2>No employee record on file</h2>
          <p>Your account isn't linked to an employee profile yet. Please contact HR.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={s.page}>
      {/* ── Page header ── */}
      <header className={s.pageHeader}>
        <div>
          <div className={s.eyebrow}>My Timesheet</div>
          <h1 className={s.pageTitle}>Clock in &amp; track hours</h1>
          <p className={s.pageSub}>
            Punch in for today, request overtime, and review the month at a glance.
          </p>
        </div>

        <div className={s.periodPicker}>
          <button type="button" className={s.periodNav}
                  onClick={() => {
                    if (month === 0) { setMonth(11); setYear(y => y - 1); }
                    else setMonth(m => m - 1);
                  }} aria-label="Previous month">‹</button>
          <div className={s.periodLabel}>
            <span className={s.periodMonth}>{MONTHS[month]}</span>
            <span className={s.periodYear}>{year}</span>
          </div>
          <button type="button" className={s.periodNav}
                  onClick={() => {
                    if (month === 11) { setMonth(0); setYear(y => y + 1); }
                    else setMonth(m => m + 1);
                  }} aria-label="Next month">›</button>
        </div>
      </header>

      {error && <div className={s.error}>{error}</div>}

      {/* "Your timesheet was edited by …" banner. Rendered above the
          clock-in hero so the user sees activity on their record before
          they punch the next button. */}
      <NotificationBanner entityTypes={["TIMESHEET"]} />

      {/* ── Clock-in hero card ── */}
      <section className={`${s.hero} ${s[`hero_${statusKind}`]}`}>
        <div className={s.heroGlow} aria-hidden="true" />

        <div className={s.heroLeft}>
          <div className={s.heroEyebrow}>
            <span className={`${s.statusDot} ${s[`dot_${statusKind}`]}`} />
            {statusKind === "out"  && "Not clocked in yet"}
            {statusKind === "in"   && "You're clocked in"}
            {statusKind === "done" && "Day complete"}
          </div>
          <div className={s.heroClock}>{clock.time}</div>
          <div className={s.heroDate}>{clock.date}</div>

          {todayRow && (
            <div className={s.heroSubmeta}>
              {todayRow.timeIn  && <span><strong>In:</strong> {fmtTime(todayRow.timeIn)}</span>}
              {todayRow.timeOut && <span><strong>Out:</strong> {fmtTime(todayRow.timeOut)}</span>}
              {todayRow.workedHours > 0 && (
                <span><strong>Worked:</strong> {todayRow.workedHours.toFixed(2)}h</span>
              )}
            </div>
          )}
        </div>

        <div className={s.heroRight}>
          {statusKind === "out" && (
            <button className={s.btnClockIn} onClick={clockIn} disabled={busy}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M8 5v14l11-7L8 5z" />
              </svg>
              {busy ? "Clocking in…" : "Clock In"}
            </button>
          )}
          {statusKind === "in" && (
            <button className={s.btnClockOut} onClick={clockOut} disabled={busy}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <rect x="6" y="6" width="12" height="12" rx="1.5" />
              </svg>
              {busy ? "Clocking out…" : "Clock Out"}
            </button>
          )}
          {statusKind === "done" && (
            <div className={s.doneBadge}>
              <span className={s.doneCheck}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m5 12 5 5L20 7" />
                </svg>
              </span>
              All set for today
            </div>
          )}
        </div>
      </section>

      {/* ── Stat tiles ── */}
      <section className={s.statsGrid}>
        <div className={s.statCard}>
          <div className={`${s.statIcon} ${s.accentA}`}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" />
            </svg>
          </div>
          <div>
            <div className={s.statLabel}>Hours this month</div>
            <div className={s.statValue}>{totals.worked.toFixed(1)}<span className={s.statSuffix}>h</span></div>
          </div>
        </div>
        <div className={s.statCard}>
          <div className={`${s.statIcon} ${s.accentB}`}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M13 2 4 14h7l-1 8 9-12h-7l1-8Z" />
            </svg>
          </div>
          <div>
            <div className={s.statLabel}>Overtime (approved)</div>
            <div className={s.statValue}>{totals.otApproved.toFixed(1)}<span className={s.statSuffix}>h</span></div>
          </div>
        </div>
        <div className={s.statCard}>
          <div className={`${s.statIcon} ${s.accentC}`}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="m4.5 12.5 5 5 10-11" />
            </svg>
          </div>
          <div>
            <div className={s.statLabel}>Days present</div>
            <div className={s.statValue}>{totals.daysPresent}</div>
          </div>
        </div>
        <div className={s.statCard}>
          <div className={`${s.statIcon} ${s.accentD}`}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M5 4h14M5 20h14M7 4l5 6-5 10h10l-5-10 5-6" />
            </svg>
          </div>
          <div>
            <div className={s.statLabel}>OT logged (any status)</div>
            <div className={s.statValue}>{totals.ot.toFixed(1)}<span className={s.statSuffix}>h</span></div>
          </div>
        </div>
      </section>

      {/* ── Entries card — toggles between List table and Calendar grid.
              Both views render from the same `rows` dataset (no refetches),
              and the per-row Request-OT / Cancel actions stay in the
              List view since they're easier to discover there. */}
      <section className={s.card}>
        <div className={s.cardHead}>
          <h3 className={s.cardTitle}>Entries — {MONTHS[month]} {year}</h3>

          <div className={s.viewTabs} role="tablist" aria-label="View mode">
            <button type="button"
                    role="tab"
                    aria-selected={viewMode === "list"}
                    className={`${s.viewTab} ${viewMode === "list" ? s.viewTabActive : ""}`}
                    onClick={() => setViewMode("list")}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
                <line x1="8" y1="6" x2="21" y2="6" />
                <line x1="8" y1="12" x2="21" y2="12" />
                <line x1="8" y1="18" x2="21" y2="18" />
                <line x1="3" y1="6"  x2="3.01" y2="6" />
                <line x1="3" y1="12" x2="3.01" y2="12" />
                <line x1="3" y1="18" x2="3.01" y2="18" />
              </svg>
              List
            </button>
            <button type="button"
                    role="tab"
                    aria-selected={viewMode === "calendar"}
                    className={`${s.viewTab} ${viewMode === "calendar" ? s.viewTabActive : ""}`}
                    onClick={() => setViewMode("calendar")}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8"  y1="2" x2="8"  y2="6" />
                <line x1="3"  y1="10" x2="21" y2="10" />
              </svg>
              Calendar
            </button>
          </div>

          <span className={s.cardSub}>{rows.length} record{rows.length === 1 ? "" : "s"}</span>
        </div>

        {loading && <div className={s.tableEmpty}>Loading…</div>}
        {!loading && rows.length === 0 && viewMode === "list" && (
          <div className={s.tableEmpty}>No timesheet entries for this period.</div>
        )}

        {/* ── List view (table) ── */}
        {!loading && rows.length > 0 && viewMode === "list" && (
          <div className={s.tableWrap}>
            <table className={s.table}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Time in</th>
                  <th>Time out</th>
                  <th className={s.right}>Worked</th>
                  <th className={s.right}>Overtime</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows
                  .slice()
                  .sort((a, b) => String(b.date).localeCompare(String(a.date)))
                  .map((r, i) => {
                    const ot = r.overtimeHours || 0;
                    const otStatus = (r.overtimeStatus || "").toUpperCase();
                    return (
                      <tr key={r.id || i} style={{ animationDelay: `${i * 30}ms` }}>
                        <td>{r.date}</td>
                        <td>{fmtTime(r.timeIn)}</td>
                        <td>{fmtTime(r.timeOut)}</td>
                        <td className={s.right}>{(r.workedHours || 0).toFixed(2)}</td>
                        <td className={s.right}>
                          {ot > 0
                            ? <span className={
                                otStatus === "APPROVED" ? s.pillOk :
                                otStatus === "REJECTED" ? s.pillBad :
                                otStatus === "PENDING"  ? s.pillWait :
                                s.pillNeutral
                              }>
                                {ot.toFixed(1)}h {otStatus || "DRAFT"}
                              </span>
                            : "—"}
                        </td>
                        <td>
                          <span className={
                            r.status === "Leave"   ? s.statusLeave :
                            r.status === "Holiday" ? s.statusHoliday :
                            r.status === "Absent"  ? s.statusAbsent :
                            s.statusOk
                          }>
                            {r.status || "Worked"}
                          </span>
                        </td>
                        <td className={s.right}>
                          {ot > 0 && otStatus === "" && (
                            <button className={s.btnLink} onClick={() => requestOvertime(r)} disabled={busy}>
                              Request OT
                            </button>
                          )}
                          {otStatus === "PENDING" && (
                            <button className={`${s.btnLink} ${s.btnLinkMuted}`}
                                    onClick={() => cancelOvertime(r)} disabled={busy}>
                              Cancel
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Calendar view (month grid) ──
            One cell per day. Cells with a timesheet row show worked
            hours + a status pill + an OT pill (when applicable). The
            grid is read-only here on purpose: editing actions stay in
            the List view to avoid duplicating action surfaces and the
            associated bug-risk + security review surface. */}
        {!loading && viewMode === "calendar" && (
          <div className={s.tsCalWrap} key={`${year}-${month}`}>
            <div className={s.tsDow}>
              {DOW.map(d => <div key={d} className={s.tsDowCell}>{d}</div>)}
            </div>
            <div className={s.tsGrid}>
              {grid.map((cell, idx) => {
                const r = rowsByDate.get(cell.iso);
                const isToday = cell.iso === todayIso;
                const col = idx % 7;
                const isWeekend = col === 5 || col === 6;
                const ot = (r?.overtimeHours) || 0;
                const otStatus = (r?.overtimeStatus || "").toUpperCase();
                const status = r?.status || (r ? "Worked" : "");
                const cellClass = [
                  s.tsCell,
                  cell.muted ? s.tsCellMuted : "",
                  isToday && !cell.muted ? s.tsCellToday : "",
                  isWeekend ? s.tsCellWeekend : "",
                  r ? s.tsCellHas : "",
                  status === "Leave"   ? s.tsCellLeave   :
                  status === "Holiday" ? s.tsCellHoliday :
                  status === "Absent"  ? s.tsCellAbsent  : "",
                ].filter(Boolean).join(" ");

                return (
                  <div key={cell.iso + idx}
                       className={cellClass}
                       style={{ animationDelay: `${idx * 8}ms` }}
                       title={r
                         ? `${cell.iso} · ${status}${r.workedHours ? ` · ${r.workedHours.toFixed(2)}h` : ""}`
                         : cell.iso}>
                    <div className={s.tsCellHead}>
                      <span className={s.tsCellNum}>{cell.day}</span>
                      {isToday && !cell.muted && <span className={s.tsTodayDot} aria-hidden="true" />}
                    </div>
                    {r && (
                      <div className={s.tsCellBody}>
                        {(r.workedHours > 0) && (
                          <div className={s.tsHours}>
                            <strong>{r.workedHours.toFixed(1)}</strong>
                            <span className={s.tsHoursUnit}>h</span>
                          </div>
                        )}
                        {status && status !== "Worked" && (
                          <span className={
                            status === "Leave"   ? s.tsStatusLeave :
                            status === "Holiday" ? s.tsStatusHoliday :
                            status === "Absent"  ? s.tsStatusAbsent :
                            s.tsStatusOk
                          }>{status}</span>
                        )}
                        {r.timeIn && status === "Worked" && (
                          <div className={s.tsTimes}>
                            {fmtTime(r.timeIn)}{r.timeOut ? ` – ${fmtTime(r.timeOut)}` : ""}
                          </div>
                        )}
                        {ot > 0 && (
                          <span className={
                            otStatus === "APPROVED" ? s.tsOtOk :
                            otStatus === "REJECTED" ? s.tsOtBad :
                            otStatus === "PENDING"  ? s.tsOtWait :
                            s.tsOtDraft
                          }>OT {ot.toFixed(1)}h</span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className={s.tsLegend}>
              <span className={s.tsLegItem}><span className={`${s.tsLegDot} ${s.tsLegPresent}`} />Present</span>
              <span className={s.tsLegItem}><span className={`${s.tsLegDot} ${s.tsLegLeave}`} />Leave</span>
              <span className={s.tsLegItem}><span className={`${s.tsLegDot} ${s.tsLegHoliday}`} />Holiday</span>
              <span className={s.tsLegItem}><span className={`${s.tsLegDot} ${s.tsLegAbsent}`} />Absent</span>
              <span className={s.tsLegItem}><span className={`${s.tsLegDot} ${s.tsLegOt}`} />Overtime</span>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
